import { useParams, Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Printer, ArrowLeft } from "lucide-react";
import { school } from "@/data/greenwood";
import { student, studentCareerMatches, parentConversationStarters, counsellorQueue } from "@/data/greenwoodPersonas";

type Audience = "student" | "parent" | "counsellor";

const COPY: Record<Audience, { title: string; blurb: string; back: string }> = {
  student: {
    title: "Learner one-pager",
    blurb: "A single page you can hand to a family at a parent-teacher meeting.",
    back: "/schools/student",
  },
  parent: {
    title: "Family one-pager",
    blurb: "Progress, not raw scores — plus three conversations to have this week.",
    back: "/schools/parent",
  },
  counsellor: {
    title: "Weekly counsellor brief",
    blurb: "Your prioritised caseload for the week, ready to print.",
    back: "/schools/counsellor",
  },
};

export default function SchoolsOnePager() {
  const params = useParams();
  const audience = (["student", "parent", "counsellor"].includes(params.audience ?? "")
    ? params.audience
    : "student") as Audience;
  const copy = COPY[audience];

  return (
    <>
      <PageHead
        title={`${copy.title} | WeKIT Education Intelligence`}
        description={copy.blurb}
        path={`/schools/${audience}/onepager`}
      />
      <div className="container max-w-3xl py-10 space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3 print:hidden">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">{copy.title}</h1>
            <p className="text-muted-foreground mt-1">{copy.blurb}</p>
          </div>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="ghost">
              <Link to={copy.back}>
                <ArrowLeft className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Back
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.print()}>
              <Printer className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Print
            </Button>
          </div>
        </header>

        <Card>
          <CardContent className="p-8 space-y-6">
            <div className="flex items-baseline justify-between gap-4 border-b pb-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{school.name}</p>
                <h2 className="text-2xl font-semibold">{copy.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground">{school.academicYear}</p>
            </div>

            {audience !== "counsellor" && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Learner</h3>
                <p className="text-lg font-medium">
                  {student.name} · {student.grade}
                </p>
              </section>
            )}

            {audience === "student" && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Career matches
                </h3>
                <ul className="space-y-1 text-sm">
                  {studentCareerMatches.map((m) => (
                    <li key={m.title} className="flex justify-between border-b py-1">
                      <span>
                        {m.title}
                        <span className="text-muted-foreground"> · {m.cluster}</span>
                      </span>
                      <span className="tabular-nums text-muted-foreground">{m.fit}% fit</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {audience === "parent" && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Conversations to have this week
                </h3>
                <ul className="space-y-2 text-sm">
                  {parentConversationStarters.map((c) => (
                    <li key={c} className="flex gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {audience === "counsellor" && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Prioritised caseload
                </h3>
                <ul className="space-y-1 text-sm">
                  {counsellorQueue.map((q) => (
                    <li key={q.id} className="border-b py-1">
                      <span className="font-medium">{q.name}</span>
                      <span className="text-muted-foreground"> · {q.grade} · {q.priority} priority</span>
                      <span className="block text-muted-foreground">{q.signal}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <p className="text-xs text-muted-foreground border-t pt-4">
              Generated by WeKIT Education Intelligence™ · demo tenant data
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
